import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';

const B = { primary: '#4357AD', dark: '#1E2A4A' };

// Дзеркало серверних лімітів app.homework.* (application.properties):
// фронтенд конфіг не читає — при зміні на сервері оновити й тут.
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 5;
const ALLOWED_EXTENSIONS = new Set([
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf', 'odt',
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'zip', 'rar', '7z',
  'mp3', 'm4a', 'mp4',
]);
const FILE_ACCEPT = [...ALLOWED_EXTENSIONS].map((ext) => `.${ext}`).join(',');

const validateHomeworkFile = (file) => {
  const ext = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
  if (!ALLOWED_EXTENSIONS.has(ext)) {
    return 'недозволений тип файлу';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'файл завеликий (максимум 10 МБ)';
  }
  return null;
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    '& fieldset': { borderColor: '#EDE9FE' },
    '&:hover fieldset': { borderColor: B.primary },
    '&.Mui-focused fieldset': { borderColor: B.primary },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: B.primary },
};

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const LESSON_FORMATS = [
  { value: 'INDIVIDUAL', label: 'Індивідуальний' },
  { value: 'PAIR', label: 'Парний' },
  { value: 'GROUP', label: 'Група' },
];

const buildSchema = (enablePayment, noHomework) =>
  yup.object({
    date: yup.string().required('Оберіть дату'),
    topic: yup.string().required('Тема обовʼязкова'),
    notes: yup.string().nullable(),
    homeworkTask: yup.string().nullable(),
    lessonFormat: yup.string().oneOf(LESSON_FORMATS.map((f) => f.value)).nullable(),
    homeworkScore: noHomework
      ? yup.mixed().nullable()
      : yup.number().min(1).max(10).required('1-10'),
    activityScore: yup.number().min(1).max(10).required('1-10'),
    behaviorScore: yup.number().min(1).max(10).required('1-10'),
    comprehensionScore: yup.number().min(1).max(10).required('1-10'),
    paid: enablePayment ? yup.boolean().required() : yup.boolean().optional(),
  });

const ScoreButtons = ({ name, label, control, disabled }) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => {
      const invalid = Boolean(fieldState.error) && !disabled;
      return (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={600} sx={{ color: disabled ? '#B0B8C9' : invalid ? '#EF4444' : B.dark, mb: 0.5, display: 'block' }}>
            {label}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {SCORES.map((n) => {
              const selected = Number(field.value) === n && !disabled;
              return (
                <Box
                  key={n}
                  onClick={() => !disabled && field.onChange(n)}
                  sx={{
                    flex: 1,
                    height: 30,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: disabled ? 'default' : 'pointer',
                    transition: 'all 0.1s',
                    bgcolor: disabled ? '#F1F5F9' : selected ? B.primary : '#fff',
                    color: disabled ? '#C4B5FD' : selected ? '#fff' : B.dark,
                    border: selected ? `2px solid ${B.primary}` : invalid ? '1.5px solid #FCA5A5' : '1.5px solid #EDE9FE',
                    opacity: disabled ? 0.5 : 1,
                    '&:hover': disabled ? {} : { borderColor: B.primary, bgcolor: selected ? B.primary : '#F0EDFF' },
                  }}
                >
                  {n}
                </Box>
              );
            })}
          </Stack>
          {invalid && (
            <Typography variant="caption" sx={{ color: '#EF4444', mt: 0.25, display: 'block' }}>
              Оберіть оцінку 1–10
            </Typography>
          )}
        </Box>
      );
    }}
  />
);

const LessonFormDialog = ({ open, onClose, initialValues, onSubmit, title, enablePayment = false }) => {
  const [noHomework, setNoHomework] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [removedFileIds, setRemovedFileIds] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const schema = useMemo(() => buildSchema(enablePayment, noHomework), [enablePayment, noHomework]);
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { paid: false, ...initialValues },
  });

  useEffect(() => {
    if (open) {
      // homeworkScore === 0 means "no homework" was explicitly set
      const isNoHw = initialValues?.homeworkScore === 0;
      setNoHomework(isNoHw);
      setPendingFiles([]);
      setRemovedFileIds([]);
      reset({ paid: false, ...initialValues, homeworkScore: isNoHw ? 0 : (initialValues?.homeworkScore || '') });
    }
  }, [initialValues, open, reset]);

  const existingFiles = (initialValues?.homeworkFiles || []).filter((f) => !removedFileIds.includes(f.id));
  const totalFileCount = existingFiles.length + pendingFiles.length;

  const handleFilesSelected = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = ''; // дозволяє повторно вибрати той самий файл
    let count = totalFileCount;
    const accepted = [];
    for (const file of picked) {
      if (count >= MAX_FILES) {
        enqueueSnackbar(`Максимум ${MAX_FILES} файлів на урок`, { variant: 'error' });
        break;
      }
      const error = validateHomeworkFile(file);
      if (error) {
        enqueueSnackbar(`${file.name}: ${error}`, { variant: 'error' });
        continue;
      }
      accepted.push(file);
      count += 1;
    }
    if (accepted.length) {
      setPendingFiles((prev) => [...prev, ...accepted]);
    }
  };

  const hw = watch('homeworkScore');
  const act = watch('activityScore');
  const beh = watch('behaviorScore');
  const comp = watch('comprehensionScore');

  const avg = useMemo(() => {
    const scores = [];
    if (!noHomework && Number(hw)) scores.push(Number(hw));
    if (Number(act)) scores.push(Number(act));
    if (Number(beh)) scores.push(Number(beh));
    if (Number(comp)) scores.push(Number(comp));
    return scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
  }, [hw, act, beh, comp, noHomework]);

  const handleFormSubmit = handleSubmit(async (values) => {
    const hwVal = noHomework ? 0 : (Number(values.homeworkScore) || null);
    const actVal = Number(values.activityScore) || null;
    const behVal = Number(values.behaviorScore) || null;
    const compVal = Number(values.comprehensionScore) || null;
    const payload = {
      ...values,
      homeworkScore: hwVal,
      activityScore: actVal,
      behaviorScore: behVal,
      comprehensionScore: compVal,
    };
    const scores = [];
    if (hwVal && hwVal > 0) scores.push(hwVal);
    if (actVal) scores.push(actVal);
    if (behVal) scores.push(behVal);
    if (compVal) scores.push(compVal);
    payload.finalScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
    // завжди рядок ('' очищає поле через null-guard оновлення, як notes)
    payload.homeworkTask = values.homeworkTask ?? '';
    delete payload.homeworkFiles;
    if (!enablePayment) delete payload.paid;
    else payload.paid = Boolean(values.paid);
    await onSubmit(payload, { pendingFiles, removedFileIds });
    onClose();
  });

  const handleNoHomeworkToggle = () => {
    const next = !noHomework;
    setNoHomework(next);
    if (next) setValue('homeworkScore', 0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 20px 60px rgba(67,87,173,0.18)', overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{ background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)`, px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <MenuBookIcon sx={{ color: '#fff', fontSize: 20 }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>{title}</Typography>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Stack spacing={2}>
          {/* Date + Topic */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Дата"
                  format="DD.MM.YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(val) => field.onChange(val ? val.format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { size: 'small', fullWidth: true, error: Boolean(errors.date), helperText: errors.date?.message, sx: { ...fieldSx, flex: '0 0 170px' } } }}
                />
              )}
            />
            <TextField label="Тема уроку" size="small" {...register('topic')} error={Boolean(errors.topic)} helperText={errors.topic?.message} fullWidth sx={fieldSx} />
          </Stack>

          {/* Lesson format */}
          <TextField
            label="Формат заняття"
            select
            size="small"
            defaultValue={initialValues?.lessonFormat || 'INDIVIDUAL'}
            {...register('lessonFormat')}
            sx={{ ...fieldSx, maxWidth: 220 }}
          >
            {LESSON_FORMATS.map((f) => (
              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
            ))}
          </TextField>

          {/* Notes */}
          <TextField label="Примітки" size="small" multiline minRows={2} {...register('notes')} fullWidth sx={fieldSx} />

          {/* Homework task + attachments */}
          <Box>
            <TextField
              label="Домашнє завдання"
              placeholder="Текст завдання або посилання"
              size="small"
              multiline
              minRows={2}
              {...register('homeworkTask')}
              fullWidth
              sx={fieldSx}
            />
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
              {existingFiles.map((f) => (
                <Chip
                  key={`existing-${f.id}`}
                  size="small"
                  icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                  label={f.fileName}
                  onDelete={() => setRemovedFileIds((prev) => [...prev, f.id])}
                  sx={{ maxWidth: 200, bgcolor: '#F8F7FF', border: '1px solid #EDE9FE', fontSize: 12 }}
                />
              ))}
              {pendingFiles.map((f, index) => (
                <Chip
                  key={`pending-${index}`}
                  size="small"
                  icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                  label={f.name}
                  onDelete={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                  sx={{ maxWidth: 200, bgcolor: '#F0FDF4', border: '1px dashed #86EFAC', fontSize: 12 }}
                />
              ))}
              <Button
                component="label"
                size="small"
                startIcon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                disabled={totalFileCount >= MAX_FILES}
                sx={{ textTransform: 'none', color: B.primary, fontWeight: 600, borderRadius: 2 }}
              >
                Додати файл
                <input hidden type="file" multiple accept={FILE_ACCEPT} onChange={handleFilesSelected} />
              </Button>
            </Stack>
          </Box>

          {/* Scores */}
          <Box sx={{ px: 2, py: 1.5, borderRadius: 3, bgcolor: '#F8F7FF', border: '1px solid #EDE9FE' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark }}>Оцінювання</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  onClick={handleNoHomeworkToggle}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    width: 190,
                    height: 26,
                    borderRadius: 99,
                    bgcolor: '#EDE9FE',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    userSelect: 'none',
                  }}
                >
              {/* Sliding pill */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 2,
                  left: noHomework ? 'calc(50% - 2px)' : 2,
                  width: 'calc(50% - 2px)',
                  height: 24,
                  borderRadius: 99,
                  bgcolor: noHomework ? '#FEF3C7' : '#D1FAE5',
                  transition: 'left 0.25s ease',
                }}
              />
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  position: 'relative',
                  zIndex: 1,
                  fontSize: 11,
                  color: noHomework ? '#8891A5' : '#166534',
                  transition: 'color 0.2s',
                }}
              >
                Є домашнє
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  position: 'relative',
                  zIndex: 1,
                  fontSize: 11,
                  color: noHomework ? '#92400E' : '#8891A5',
                  transition: 'color 0.2s',
                }}
              >
                Без домашнього
              </Typography>
                </Box>
                {avg != null && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" sx={{ color: '#8891A5' }}>Середній:</Typography>
                    <Box sx={{ px: 1, py: 0.25, borderRadius: 1.5, bgcolor: '#EDE9FE' }}>
                      <Typography variant="caption" fontWeight={800} sx={{ color: B.primary }}>{avg}</Typography>
                    </Box>
                  </Stack>
                )}
              </Stack>
            </Stack>

            <ScoreButtons name="homeworkScore" label="Оцінка за ДЗ" control={control} disabled={noHomework} />
            <ScoreButtons name="activityScore" label="Активність" control={control} disabled={false} />
            <ScoreButtons name="behaviorScore" label="Поведінка" control={control} disabled={false} />
            <ScoreButtons name="comprehensionScore" label="Засвоєння матеріалу" control={control} disabled={false} />
          </Box>

          {/* Payment */}
          {enablePayment && (
            <FormControlLabel
              control={<Checkbox {...register('paid')} sx={{ color: '#C4B5FD', '&.Mui-checked': { color: B.primary }, py: 0 }} size="small" />}
              label={<Typography variant="body2" fontWeight={600} sx={{ color: B.dark }}>Оплачено</Typography>}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#8891A5', fontWeight: 600, borderRadius: 3, px: 2.5 }}>
          Скасувати
        </Button>
        <Button
          variant="contained"
          onClick={handleFormSubmit}
          disabled={isSubmitting}
          sx={{ textTransform: 'none', bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 3, fontWeight: 700, px: 3, boxShadow: '0 4px 14px rgba(67,87,173,0.3)' }}
        >
          Зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LessonFormDialog;
